import React, { useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [sheetTranslateY, setSheetTranslateY] = useState(0);
  const touchStartY = useRef(0);
  const isDragging = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    isDragging.current = true;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current) return;
    const deltaY = e.touches[0].clientY - touchStartY.current;
    if (deltaY > 0) {
      setSheetTranslateY(deltaY);
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    isDragging.current = false;
    if (sheetTranslateY > 100) {
      setIsMenuOpen(false);
    }
    setSheetTranslateY(0);
  }, [sheetTranslateY]);

  const menuItems = [
    { name: t('nav.solutions'), href: '/#solutions', type: 'hash' as const },
    { name: t('nav.realizations'), href: '/#realizations', type: 'hash' as const },
    { name: t('nav.about'), href: '/#about', type: 'hash' as const },
    { name: t('nav.contact'), href: '/kontakt', type: 'route' as const },
  ];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const top = element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const handleLogoClick = () => {
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, item: { href: string; type: string }) => {
    e.preventDefault();
    setIsMenuOpen(false);

    if (item.type === 'route') {
      navigate(item.href);
      return;
    }

    const sectionId = item.href.replace('/#', '');
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => scrollToSection(sectionId), 100);
    } else {
      scrollToSection(sectionId);
    }
  };

  return (
    <header className="fixed top-2 left-3 right-3 z-50" role="banner">
      {/* Solid background behind the navbar so content doesn't show through */}
      <div className="bg-[#0c0c0c] pb-0.5 sm:pb-1 rounded-2xl">
        <div className="bg-[#161616] border border-[#2a2a2a] rounded-2xl shadow-lg shadow-black/30">
          <div className="pl-2 pr-4 sm:px-6">
            <div className="flex items-center justify-between h-14 sm:h-20">
              {/* Logo */}
              <div
                className="flex items-center cursor-pointer"
                onClick={handleLogoClick}
                onKeyDown={(e) => { if (e.key === 'Enter') handleLogoClick(); }}
                role="link"
                tabIndex={0}
                aria-label="FBS group - home"
              >
                <img 
                  src="/logo.svg" 
                  alt="FBS group" 
                  className="h-24 sm:h-[150px]"
                />
              </div>

              {/* Burger menu + Language switcher */}
              <div className="flex items-center gap-6">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="w-10 h-10 border-2 border-accent-green rounded flex items-center justify-center text-gray-300 hover:bg-accent-green/10 hover:text-accent-green transition-all duration-200"
                  aria-label={isMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
                  aria-expanded={isMenuOpen}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    {isMenuOpen ? (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 8h16" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16h16" />
                      </>
                    )}
                  </svg>
                </button>
                <nav aria-label="Language switcher" className="flex items-center gap-1.5 text-sm">
                  <button onClick={() => { i18n.changeLanguage('sk'); }} className={`${i18n.language === 'sk' ? 'text-accent-green font-semibold' : 'text-white hover:text-accent-green'} transition-colors`}>SK</button>
                  <span className="text-gray-600">|</span>
                  <button onClick={() => { i18n.changeLanguage('en'); }} className={`${i18n.language === 'en' ? 'text-accent-green font-semibold' : 'text-white hover:text-accent-green'} transition-colors`}>EN</button>
                </nav>
              </div>
            </div>

            {/* Navigation Menu */}
            {isMenuOpen && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 bg-black/60 z-40"
                  onClick={() => setIsMenuOpen(false)}
                ></div>

                {/* Mobile/Tablet: Bottom sheet (< lg) */}
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                <div
                  className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#161616] border-t border-[#2a2a2a] rounded-t-2xl px-6 pt-6 pb-8 animate-slide-up"
                  style={{
                    transform: sheetTranslateY > 0 ? `translateY(${sheetTranslateY}px)` : undefined,
                    transition: sheetTranslateY > 0 ? 'none' : 'transform 0.3s ease-out',
                  }}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <div className="w-10 h-1 bg-gray-600 rounded-full mx-auto mb-6 cursor-grab"></div>
                  <nav aria-label="Mobile navigation" className="space-y-1">
                    {menuItems.map((item) => (
                      <a
                        key={item.name}
                        href={item.href}
                        className="block px-3 py-3 text-lg text-gray-300 hover:text-accent-green hover:bg-tech-gray rounded-md transition-colors duration-200"
                        onClick={(e) => handleClick(e, item)}
                      >
                        {item.name}
                      </a>
                    ))}
                    <div className="pt-4">
                      <button onClick={() => { setIsMenuOpen(false); navigate('/servis'); }} className="btn-primary text-sm w-full !text-black">
                        {t('nav.serviceCall')}
                      </button>
                    </div>
                    <div className="flex items-center justify-center gap-3 pt-4 text-sm">
                      <button onClick={() => { i18n.changeLanguage('sk'); }} className={`${i18n.language === 'sk' ? 'text-accent-green font-semibold' : 'text-white hover:text-accent-green'} transition-colors`}>SK</button>
                      <span className="text-gray-600">|</span>
                      <button onClick={() => { i18n.changeLanguage('en'); }} className={`${i18n.language === 'en' ? 'text-accent-green font-semibold' : 'text-white hover:text-accent-green'} transition-colors`}>EN</button>
                    </div>
                  </nav>
                </div>

                {/* Desktop/Laptop: Slide-in from right (lg+) */}
                <div
                  className="hidden lg:flex fixed top-0 right-0 h-full w-80 z-50 bg-[#161616] border-l border-[#2a2a2a] shadow-2xl shadow-black/50 animate-slide-right flex-col px-8 pt-24 pb-8"
                >
                  {/* Close button */}
                  <button
                    onClick={() => setIsMenuOpen(false)}
                    className="absolute top-6 right-6 text-gray-400 hover:text-white transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>

                  <nav aria-label="Desktop navigation" className="space-y-2 flex-1">
                    {menuItems.map((item) => (
                      <a
                        key={item.name}
                        href={item.href}
                        className="block px-4 py-3 text-lg text-gray-300 hover:text-accent-green hover:bg-tech-gray rounded-lg transition-colors duration-200"
                        onClick={(e) => handleClick(e, item)}
                      >
                        {item.name}
                      </a>
                    ))}
                    <div className="pt-6">
                      <button onClick={() => { setIsMenuOpen(false); navigate('/servis'); }} className="btn-primary text-sm w-full !text-black">
                        {t('nav.serviceCall')}
                      </button>
                    </div>
                  </nav>

                  <div className="flex items-center justify-center gap-3 text-sm border-t border-[#2a2a2a] pt-6">
                    <button onClick={() => { i18n.changeLanguage('sk'); }} className={`${i18n.language === 'sk' ? 'text-accent-green font-semibold' : 'text-white hover:text-accent-green'} transition-colors`}>SK</button>
                    <span className="text-gray-600">|</span>
                    <button onClick={() => { i18n.changeLanguage('en'); }} className={`${i18n.language === 'en' ? 'text-accent-green font-semibold' : 'text-white hover:text-accent-green'} transition-colors`}>EN</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
